import { useRef, useState } from "react";
import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";
import { Role } from "../../Models";
import { Link, useNavigate } from "react-router-dom";
import ConfirmModal from "../Shared/ConfirmModal";
import useApiToken from "../../hooks/useApiToken";
import { dateConverter } from "../../Helpers";
import { clearLocalStorageExceptDark } from "../TextEditor/textEditorConfig";
import { careerDomain } from "../../helpers/constants";
import { CareersUrl } from "../../config/appConfig";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { CalendarDaysIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { Spinner } from "../Shared/Spinner";
import { useNotification } from "../../context/NotificationContext";
import Loading from "../Shared/Loading";
import { DeleteRole, UpdateRole } from "../../api/roles.api";
import { ExtractCVDataForRole } from "../../api/ai.api";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export default function RoleCard({
  role,
  isRightSidebarOpen,
  selectedRoles,
  handleSelect,
}: {
  role: Role;
  isRightSidebarOpen: boolean;
  selectedRoles: { id: string; iscancel: boolean }[];
  handleSelect: Function;
}) {
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);
  const [cancelModalIsOpen, setCancelModalIsOpen] = useState<boolean>(false);
  const [deleteCandidateModalIsOpen, setDeleteCandidateModalIsOpen] =
    useState<boolean>(false);
  const [jobStatusModalIsOpen, setJobStatusModalIsOpen] =
    useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(role.isActive);
  const [isCancel, setIsCancel] = useState<boolean>(role.iscancel);
  const [loading, setLoading] = useState<boolean>(false);
  const { success } = useNotification();
  const token = useApiToken();
  const navigate = useNavigate();
  const menuItemsRef = useRef<HTMLDivElement>(null);

  const deleteOnConfirm = async () => {
    if (!token) return;
    await DeleteRole(role.id, token, true);
    window.location.reload();
    setDeleteModalIsOpen(false);
  };

  const cancelOnConfirm = async () => {
    if (!token) return;
    const body = [
      {
        propertyName: "Iscancel",
        propertyValue: "true",
      },
      {
        propertyName: "IsActive",
        propertyValue: String(false),
      },
    ];
    await UpdateRole(role.id, body, token);
    setIsCancel(true);
    setCancelModalIsOpen(false);
    window.location.reload();
  };

  const deleteCandidateOnConfirm = async () => {
    if (!token) return;
    await DeleteRole(role.id, token, false);
    window.location.reload();
    setDeleteCandidateModalIsOpen(false);
  };

  const closeOnConfirm = async () => {
    if (!token) return;
    const body = [
      {
        propertyName: "IsActive",
        propertyValue: String(!isActive),
      },
    ];
    await UpdateRole(role.id, body, token);
    setIsActive(!isActive);
    setJobStatusModalIsOpen(false);
  };

  const colors = [
    "bg-red-500",
    "bg-red-600",
    "bg-red-700",
    "bg-blue-500",
    "bg-blue-600",
    "bg-blue-700",
    "bg-orange-500",
    "bg-orange-600",
    "bg-orange-700",
    "bg-purple-500",
    "bg-purple-600",
    "bg-purple-700",
    "bg-green-500",
    "bg-green-600",
    "bg-green-700",
    "bg-yellow-500",
    "bg-yellow-600",
    "bg-yellow-700",
    "bg-indigo-500",
    "bg-indigo-600",
    "bg-indigo-700",
    "bg-teal-500",
    "bg-teal-600",
    "bg-teal-700",
  ];

  const validateCVData = async () => {
    setLoading(true);
    await ExtractCVDataForRole(role.id, token || "").then(() => {
      setLoading(false);
      success("Success", "CV data successfully validated.");
    });
  };

  const getColorBySkill = (skillName: string) => {
    let hash = 0;
    for (let i = 0; i < skillName.length; i++) {
      hash = skillName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
  };

  return (
    <>
      {/* toggleView ? (
        loading ? (
          <div className="mx-auto max-w-7xl sm:px-6 lg:px-8 dark:border-gray-800 flex flex-row items-center gap-2 dark:text-gray-300 text-gray-700 p-2">
            <div className="flex flex-col justify-center items-center h-[40px]">
              <Spinner height="100px" />
            </div>
            <div className="flex flex-row justify-center items-center h-[40px]">
              <div>
                Please wait while we validate the CV data. This might take a few
                minutes.
              </div>
            </div>
          </div>
        ) : (
          <div className="border-b border-gray-200 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8  dark:border-gray-800">
            <div
              className="sm:flex sm:items-baseline sm:justify-between hover:bg-gradient-to-r from-white via-gray-50 to-white pl-2 py-4 rounded-sm dark:from-darkbg dark:via-darkbglight dark:to-darkbg cursor-pointer "
              onClick={() => {
                navigate(`/roles/${role.id}`);
              }}
            >
              <div className=" grid grid-cols-3 sm:w-full sm:flex-1">
                <div className="col-span-2">
                  <h1
                    id="message-heading"
                    className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-300"
                  >
                    <span className="flex flex-row  items-center gap-2">
                      {role.title}{" "}
                      {isActive && role.isEmailStopped && (
                        <ExclamationTriangleIcon
                          color="orange"
                          height={20}
                          title="One or more assessment set is out of bounds. Email has been stopped! Please Recalibrate the Set"
                        />
                      )}
                    </span>
                  </h1>
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <MapPinIcon
                      className="mr-1.5 h-5 w-5 flex-shrink-0 text-entntblue"
                      aria-hidden="true"
                    />
                    {role.location}
                  </div>
                </div>
                <div>
                  <h1
                    id="message-heading"
                    className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-400"
                  >
                    <span> Created on</span>
                  </h1>
                  <div className="mt-2 flex items-center text-sm text-gray-500 ">
                    <CalendarDaysIcon
                      className="mr-1.5 h-5 w-5 flex-shrink-0 text-entntblue"
                      aria-hidden="true"
                    />
                    {dateConverter(role.date)}
                  </div>
                </div>
              </div>

              <div className="mt-4 mb-0 flex items-center justify-between sm:ml-6 sm:mt-0 sm:flex-shrink-0 sm:justify-start">
                {isActive ? (
                  <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                    Open
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                    {isCancel ? "Cancel" : "Close"}
                  </span>
                )}

                <Menu as="div" className="relative ml-3 inline-block text-left">
                  <div>
                    <Menu.Button
                      className="-my-2 flex items-center rounded-full bg-transparent  text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                      }}
                    >
                      <span className="sr-only">Open options</span>
                      <EllipsisVerticalIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items
                      className={`absolute right-16 z-10 mt-4 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-darkbglight ${isRightSidebarOpen ? "mr-[19rem]" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      ref={menuItemsRef}
                    >
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to={`/roles/${role.id}/edit`}
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "block px-4 py-2 text-sm"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                clearLocalStorageExceptDark();
                              }}
                            >
                              Edit
                            </Link>
                          )}
                        </Menu.Item>
                        {!isCancel && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                className={classNames(
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700 dark:text-gray-400",
                                  "flex w-full justify-between px-4 py-2 text-sm"
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setJobStatusModalIsOpen(true);
                                }}
                              >
                                <span>{isActive ? "Close" : "Open"}</span>
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        {!isCancel && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                className={classNames(
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700 dark:text-gray-400",
                                  "flex w-full justify-between px-4 py-2 text-sm"
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCancelModalIsOpen(true);
                                }}
                              >
                                <span>Cancel</span>
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        {isCancel && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                className={classNames(
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700 dark:text-gray-400",
                                  "flex w-full justify-between px-4 py-2 text-sm"
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteCandidateModalIsOpen(true);
                                }}
                              >
                                <span>Remove all related candidate</span>
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "block w-full px-4 py-2 text-left text-sm"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModalIsOpen(true);
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to={`/roles/${role.id}/create`}
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "block w-full px-4 py-2 text-left text-sm"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                clearLocalStorageExceptDark();
                              }}
                            >
                              Create a copy
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <div
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "block w-full px-4 py-2 text-left text-sm cursor-pointer"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                  `${careerDomain}/${role.id}/overview`
                                );
                              }}
                            >
                              Copy role url
                            </div>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <div
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "block w-full px-4 py-2 text-left text-sm cursor-pointer"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                  `${CareersUrl}/entnt-internship/${role.id}`
                                );
                                success("Success", "Internship URL copied to clipboard");
                              }}
                            >
                              Copy Internship URL
                            </div>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <div
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "flex w-full justify-between px-4 py-2 text-sm"
                              )}
                              onClick={validateCVData}
                            >
                              <span>Validate CV Data</span>
                            </div>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
        )
      ) : ( */}
        <>
          <tr
            className="bg-white border-b dark:bg-darkbg dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer "
            onClick={() => navigate(`/roles/${role.id}`)}
          >
            <td className="px-4 py-2">
              <input
                type="checkbox"
                checked={selectedRoles.some((r) => r.id === role.id)}
                className="form-checkbox h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(role.id);
                }}
              />
            </td>
            <td className="py-2 font-medium text-gray-900 whitespace-nowrap dark:text-white max-w-[400px]">
              <div className="flex items-center">
                {!loading ? (
                  <span className="truncate block max-w-[370px]" title={role.title}>{role.title}</span>
                ) : (
                  <div className="flex flex-row gap-2">
                    {" "}
                    <Spinner height="10px" /> <small>Validating CV Data</small>
                  </div>
                )}{" "}
                &nbsp;
                {isActive && role.isEmailStopped && (
                  <ExclamationTriangleIcon
                    color="orange"
                    height={20}
                    title="One or more assessment set is out of bounds. Email has been stopped! Please Recalibrate the Set"
                    className="ml-2"
                  />
                )}
              </div>
            </td>
            <td
              className="py-2 hidden sm:table-cell text-center truncate"
              style={{ maxWidth: "150px" }}
              title={role.salary}
            >
              {role.salary
                ? role.salary.replace(/([A-Za-z]+)/g, (match) => ` ${match}`)
                : "N/A"}
            </td>
            <td className="py-2 hidden sm:table-cell text-center">
              {role.totalCandidates ?? 0}
            </td>
            <td className="py-2 hidden sm:table-cell text-center">
              {role.avgScore ?? 0}%
            </td>
            <td className=" hidden sm:table-cell text-center">
              <div
                className="flex flex-row flex-wrap overflow-x-auto p-2 gap-1 "
                style={{ maxHeight: "100px" }}
              >
                {role.skill && role.skill.length > 0
                  ? role.skill.map((s, index) => (
                      <div key={index} className="mr-2">
                        <span
                          className={`${getColorBySkill(s.name)} text-white px-2 py-[2px] font-semibold rounded-md text-xs truncate max-w-40`}
                        >
                          {s.name}
                        </span>
                      </div>
                    ))
                  : "N/A"}
              </div>
            </td>
            <td className="py-2 hidden sm:table-cell text-center">
              {role.location}
            </td>
            <td className="py-2 hidden sm:table-cell text-center">
              {dateConverter(role.date)}
            </td>
            <td className="py-2 hidden sm:table-cell text-center">
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  isActive
                    ? "bg-green-50 text-green-700 ring-1 ring-green-600/20"
                    : "bg-red-50 text-red-700 ring-1 ring-red-600/20"
                }`}
              >
                {isActive ? "Open" : isCancel ? "Cancel" : "Close"}
              </span>
            </td>
            <td className="px-6 py-2 ">
              <Menu as="div" className=" inline-block text-left">
                <div>
                  <Menu.Button
                    className="-my-2 flex items-center rounded-full bg-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span className="sr-only ">Open options</span>
                    <EllipsisVerticalIcon
                      className="h-5 w-5 pt-2"
                      aria-hidden="true"
                    />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className={`absolute right-10 z-10 mt-4 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-darkbglight ${isRightSidebarOpen ? "mr-[19rem]" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    ref={menuItemsRef}
                  >
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to={`/roles/${role.id}/edit`}
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700 dark:text-gray-400",
                              "block px-4 py-2 text-sm"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              clearLocalStorageExceptDark();
                            }}
                          >
                            Edit
                          </Link>
                        )}
                      </Menu.Item>
                      {!isCancel && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "flex w-full justify-between px-4 py-2 text-sm"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setJobStatusModalIsOpen(true);
                              }}
                            >
                              <span>{isActive ? "Close" : "Open"}</span>
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      {!isCancel && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "flex w-full justify-between px-4 py-2 text-sm"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCancelModalIsOpen(true);
                              }}
                            >
                              <span>Cancel</span>
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      {isCancel && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 dark:text-gray-400",
                                "flex w-full justify-between px-4 py-2 text-sm"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteCandidateModalIsOpen(true);
                              }}
                            >
                              <span>Remove all related candidate</span>
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700 dark:text-gray-400",
                              "block w-full px-4 py-2 text-left text-sm"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModalIsOpen(true);
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to={`/roles/${role.id}/create`}
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700 dark:text-gray-400",
                              "block w-full px-4 py-2 text-left text-sm"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              clearLocalStorageExceptDark();
                            }}
                          >
                            Create a copy
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <div
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700 dark:text-gray-400",
                              "block w-full px-4 py-2 text-left text-sm cursor-pointer"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(
                                `${careerDomain}/${role.id}/overview`
                              );
                            }}
                          >
                            Copy role url
                          </div>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <div
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700 dark:text-gray-400",
                              "block w-full px-4 py-2 text-left text-sm cursor-pointer"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(
                                `${CareersUrl}/entnt-internship/${role.id}`
                              );
                              success("Success", "Internship URL copied to clipboard");
                            }}
                          >
                            Copy Internship URL
                          </div>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <div
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700 dark:text-gray-400",
                              "flex w-full justify-between px-4 py-2 text-sm"
                            )}
                            onClick={validateCVData}
                          >
                            <span>Validate CV Data</span>
                          </div>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </td>
          </tr>
        </>
      <ConfirmModal
        open={deleteModalIsOpen}
        setOpen={setDeleteModalIsOpen}
        onConfirm={deleteOnConfirm}
        type="danger"
        title="Delete job"
        description=" Are you sure you want to delete this job? All of the
        data will be permanently removed. This action cannot
        be undone."
      />
      <ConfirmModal
        open={deleteCandidateModalIsOpen}
        setOpen={setDeleteCandidateModalIsOpen}
        onConfirm={deleteCandidateOnConfirm}
        type="danger"
        title="Delete all related candidates"
        description=" Are you sure you want to delete all related candidates? All of the
        data will be permanently removed. This action cannot
        be undone."
      />
      <ConfirmModal
        open={cancelModalIsOpen}
        setOpen={setCancelModalIsOpen}
        onConfirm={cancelOnConfirm}
        type="danger"
        title="Cancel job"
        description=" Are you sure you want to cancel this job? This action cannot
        be undone."
      />
      <ConfirmModal
        open={jobStatusModalIsOpen}
        setOpen={setJobStatusModalIsOpen}
        onConfirm={closeOnConfirm}
        type="info"
        title="Change Job status"
        description={`Are you sure you want make this job ${isActive ? "inactive" : "active"}?`}
      />
    </>
  );
}
